import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";
import DetailPage, { PageHeader, CtaBand } from "../components/Detail";
import { TOPIC_ORDER, pad } from "../lib/site";

export function TopicsOverview() {
  const { t } = useLanguage();
  return (
    <main id="main-content">
      <PageHeader overline={t.topics.pageOverline} title={t.topics.pageTitle} sub={t.topics.pageSub} overlineTestId="topics-page-overline" />
      <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-16 lg:px-12">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="topics-grid">
          {TOPIC_ORDER.map((slug, i) => {
            const item = t.topics.items[slug];
            const featured = slug === "trauma";
            return (
              <Reveal key={slug} delay={i * 0.04}>
                <Link
                  to={`/themen/${slug}`}
                  data-testid={`topic-card-${slug}`}
                  className={`group flex h-full flex-col justify-between rounded-3xl border p-7 transition-all duration-300 hover:-translate-y-1 ${
                    featured ? "border-petrol bg-petrol text-white" : "border-warmline bg-white hover:border-teal"
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className={`font-mono text-xs font-semibold ${featured ? "text-teal-bright" : "text-gold-dark"}`} aria-hidden="true">
                        ({pad(i + 1)})
                      </span>
                      {item.badge && (
                        <span className="rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-petrol">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <h2 className={`mt-4 font-display text-lg font-semibold tracking-tight ${featured ? "text-white" : "text-petrol"}`}>
                      {item.title}
                    </h2>
                    <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-white/75" : "text-slate2"}`}>{item.teaser}</p>
                  </div>
                  <span className={`mt-5 inline-flex items-center gap-1.5 text-sm font-bold ${featured ? "text-teal-bright" : "text-teal-dark"}`}>
                    {t.common.readMore}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-14">
          <CtaBand testId="topics-cta-band" ctaTestId="topics-whatsapp-cta" />
        </div>
      </div>
    </main>
  );
}

export function TopicDetail() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const item = t.topics.items[slug];
  if (!item) return <Navigate to="/themen" replace />;
  const related = (item.related || [])
    .filter((s) => t.topics.items[s])
    .map((s) => ({ slug: s, title: t.topics.items[s].title }));
  return (
    <DetailPage
      data={{ ...item, overline: t.topics.pageOverline }}
      basePath="/themen"
      relatedItems={related}
      relatedBasePath="/themen"
    />
  );
}
